/** Controlled vocabulary for jurisdiction site-plan requirements, shared between the
 * admin state-requirements page and the facility-facing Site Map page so labels/groupings
 * stay in sync in exactly one place. */

export const SITE_PLAN_REQUIRED_WHEN_LABELS: Record<string, string> = {
  always: 'Always',
  epcra_302_facilities_only: 'EPCRA 302 facilities only',
  new_or_updated_facility_only: 'New or updated facility only',
  on_request: 'On request',
};

export const SITE_PLAN_FORMAT_LABELS: Record<string, string> = {
  attached_to_electronic_filing: 'Attached to electronic filing',
  separate_upload: 'Separate upload',
  mailed_paper: 'Mailed paper',
  no_format_specified: 'No format specified',
};

export const SITE_PLAN_UPDATE_FREQUENCY_LABELS: Record<string, string> = {
  annual: 'Annual',
  each_filing: 'Each filing',
  on_change: 'On change',
};

export const SITE_PLAN_ELEMENT_GROUPS = [
  {
    key: 'map_metadata',
    label: 'Map metadata',
    options: [
      { value: 'map_identification', label: 'Map identification' },
      { value: 'north_arrow_and_scale', label: 'North arrow and scale' },
      { value: 'latitude_longitude', label: 'Latitude / longitude' },
      { value: 'site_grid_or_legend', label: 'Site grid or legend' },
    ],
  },
  {
    key: 'boundaries_and_structures',
    label: 'Boundaries and structures',
    options: [
      { value: 'facility_boundary', label: 'Facility boundary' },
      { value: 'buildings_and_structures', label: 'Buildings and structures' },
      { value: 'entrances_exits_and_access_roads', label: 'Entrances, exits, and access roads' },
      { value: 'adjacent_streets_and_land_use', label: 'Adjacent streets and land use' },
      { value: 'loading_and_staging_areas', label: 'Loading and staging areas' },
    ],
  },
  {
    key: 'chemical_storage',
    label: 'Chemical storage',
    options: [
      { value: 'chemical_storage_locations', label: 'Chemical storage locations' },
      { value: 'chemical_ids_and_amounts_at_locations', label: 'Chemical IDs and amounts at locations' },
      { value: 'storage_tanks', label: 'Storage tanks' },
      { value: 'container_details', label: 'Container details' },
      { value: 'piping_and_transfer_lines', label: 'Piping and transfer lines' },
      { value: 'process_and_waste_areas', label: 'Process and waste areas' },
      { value: 'secondary_containment', label: 'Secondary containment' },
    ],
  },
  {
    key: 'emergency_response',
    label: 'Emergency response',
    options: [
      { value: 'evacuation_routes_and_assembly', label: 'Evacuation routes and assembly' },
      { value: 'fire_protection_and_response_equipment', label: 'Fire protection and response equipment' },
      { value: 'water_supply_and_hydrants', label: 'Water supply and hydrants' },
      { value: 'utility_shutoffs', label: 'Utility shutoffs' },
      { value: 'emergency_contact_posting', label: 'Emergency contact posting' },
    ],
  },
  {
    key: 'environment_and_exposure',
    label: 'Environment and exposure',
    options: [
      { value: 'drains_and_drainage', label: 'Drains and drainage' },
      { value: 'nearby_receptors', label: 'Nearby receptors' },
      { value: 'prevailing_wind', label: 'Prevailing wind' },
    ],
  },
] as const;

export const REQUIRED_ATTACHMENT_GROUPS = [
  {
    key: 'site_map_and_plan',
    label: 'Site map / plan',
    options: [
      { value: 'site_plan', label: 'Site plan' },
      { value: 'site_map', label: 'Site map' },
      { value: 'facility_map', label: 'Facility map' },
      { value: 'latitude_longitude', label: 'Latitude / longitude' },
      { value: 'site_coordinate_abbreviations_list', label: 'Site coordinate abbreviations list' },
    ],
  },
  {
    key: 'sds',
    label: 'Safety data sheets (SDS)',
    options: [
      { value: 'sds', label: 'SDS' },
      { value: 'sds_all_reported_chemicals', label: 'SDS — all reported chemicals' },
      { value: 'sds_each_reported_chemical', label: 'SDS — each reported chemical' },
      { value: 'sds_new_or_updated', label: 'SDS — new or updated chemicals' },
      { value: 'sds_newly_reported_chemicals', label: 'SDS — newly reported chemicals' },
    ],
  },
  {
    key: 'emergency_and_training',
    label: 'Emergency response & training',
    options: [
      { value: 'emergency_response_plan', label: 'Emergency response plan' },
      { value: 'facility_emergency_response_plan', label: 'Facility emergency response plan' },
      {
        value: 'ehs_worksheet_or_emergency_response_plan_with_site_plan',
        label: 'EHS worksheet or ERP with site plan',
      },
      { value: 'employee_training_plan', label: 'Employee training plan' },
      { value: 'description_of_dikes_and_safeguards', label: 'Description of dikes and safeguards' },
    ],
  },
  {
    key: 'certification',
    label: 'Certification',
    options: [{ value: 'certification_letter', label: 'Certification letter' }],
  },
] as const;

const SITE_PLAN_ELEMENT_OPTIONS: { value: string; label: string }[] = SITE_PLAN_ELEMENT_GROUPS.flatMap(
  (group): { value: string; label: string }[] => [...group.options],
);

const REQUIRED_ATTACHMENT_OPTIONS: { value: string; label: string }[] = REQUIRED_ATTACHMENT_GROUPS.flatMap(
  (group): { value: string; label: string }[] => [...group.options],
);

export function sitePlanElementLabel(value: string): string {
  return SITE_PLAN_ELEMENT_OPTIONS.find((option) => option.value === value)?.label ?? value;
}

export function requiredAttachmentLabel(value: string): string {
  return REQUIRED_ATTACHMENT_OPTIONS.find((option) => option.value === value)?.label ?? value;
}

/** Attachment groups that are specifically about the site map/plan document itself,
 * as opposed to SDS, emergency response, or certification paperwork. */
export const SITE_MAP_ATTACHMENT_VALUES: ReadonlySet<string> = new Set(
  REQUIRED_ATTACHMENT_GROUPS.find((group) => group.key === 'site_map_and_plan')?.options.map((o) => o.value) ?? [],
);
